import type { components } from "../../api/schema";
import type { ChoiceListChoice } from "./ChoiceList";
import { stripInlineMarkup } from "./parseInlineMarkup";

type Segment = components["schemas"]["Segment"];
type Character = components["schemas"]["Character"];

type ResolveChoicePromptInput = {
  segments: readonly Segment[];
  choices: readonly ChoiceListChoice[];
  protagonist?: Character | undefined;
};

const URGENT_RE = /逃|急|止|守|助|危|追|隠|死|殺|血|火事|叫|罠|襲|閉じ込|失う/;
const INVESTIGATION_RE = /調べ|検証|探|見る|確かめ|証拠|現場|鍵|痕|跡|燭台|手紙|窓|机|部屋|書斎|密室/;
const DIALOGUE_RE = /尋ね|聞|問|問い|会い|話|返|証言|説得|追及|問い詰|聞き出/;
const MOVEMENT_RE = /行く|向か|進む|戻る|入る|出る|開け|近づ|離れ|追う/;
const DETECTIVE_RE = /探偵|推理|観察|事件|謎|捜査|調査/;

function includesAny(text: string, pattern: RegExp): boolean {
  return pattern.test(text);
}

function findLastDialogue(segments: readonly Segment[]): components["schemas"]["DialogueSegment"] | null {
  for (let index = segments.length - 1; index >= 0; index -= 1) {
    const segment = segments[index];
    if (segment?.type === "dialogue") return segment;
  }
  return null;
}

export function resolveChoicePrompt({
  segments,
  choices,
  protagonist,
}: ResolveChoicePromptInput): string {
  const choiceText = choices.map((choice) => stripInlineMarkup(choice.text)).join(" ");
  const lastDialogue = findLastDialogue(segments);
  const lastText =
    segments.length > 0 ? stripInlineMarkup(segments[segments.length - 1]?.text ?? "") : "";
  const contextText = `${lastText} ${choiceText}`;
  const protagonistContext = `${protagonist?.name ?? ""} ${protagonist?.role ?? ""}`;
  const isDetectiveLike = includesAny(protagonistContext, DETECTIVE_RE);

  if (includesAny(contextText, URGENT_RE)) {
    return "選ばなければ、何かを失う。";
  }

  if (lastDialogue !== null && includesAny(choiceText, DIALOGUE_RE)) {
    return `${lastDialogue.speakerName}の言葉に、どう返すべきか…`;
  }

  if (includesAny(choiceText, INVESTIGATION_RE)) {
    return isDetectiveLike
      ? "どこから確かめるべきか…"
      : "何を確かめるべきか…";
  }

  if (includesAny(choiceText, DIALOGUE_RE)) {
    return isDetectiveLike
      ? "誰の言葉から切り込むべきか…"
      : "誰の言葉を信じるべきか…";
  }

  if (includesAny(choiceText, MOVEMENT_RE)) {
    return "どこへ向かうべきか…";
  }

  return isDetectiveLike ? "次の一手を、どう打つべきか…" : "どうすべきか…";
}
